import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiHandler from "./../../../services/api/ApiHandler";
import apiPayload from './../../../services/api/ApiPayload';
import CloudLoader from './../loader/Loader_02';
import VirtualKeyboard from '../virtual/Keyboard';

const JoinUs = ({ contentData, initError, initLoader }) => {
  let { data, loading, error, handlePostApi } = ApiHandler();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 1023px)').matches);
  const [activeInput, setActiveInput] = useState(null);
  const inputRefs = useRef({});  // Refs for position
  const [step, setStep] = useState(1);  // Current step (1 to 8)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    contactNumber: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    city: '',
    state: '',
    country: '',
    referralCode: '',
    securityAnswer: '',
  });
  const [errors, setErrors] = useState({});  // Field-specific errors
  const [isLoading, setIsLoading] = useState(false);  // For API loading
  const [serverError, setServerError] = useState(null);  // For API uniqueness errors

  // Validation functions
  const validateName = (name) => /^[A-Za-z\s]{2,100}$/.test(name.trim());
  const validateContact = (contact) => /^\+\d{2}\d{10}$/.test(contact) && contact.length === 13;
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.com$/.test(email.trim());
  const validateUsername = (username) => /^[A-Za-z0-9]{1,100}$/.test(username.trim());
  const validatePassword = (password, confirm) => {
    const len = password.length;
    return len >= 5 && len <= 100 && password === confirm;
  };
  const validateLocation = (loc) => /^[A-Za-z\s,.-]{2,100}$/.test(loc.trim());
  const validateSecurityAnswer = (answer) => answer.trim().split(/\s+/).length <= 20 && answer.trim().length >= 1;

  // Validate current step's fields
  const validateCurrentStep = useCallback(() => {
    let newErrors = {};
    switch (step) {
      case 1:
        if (!validateName(formData.firstName)) newErrors.firstName = 'First name: letters only, 2-100 chars';
        if (!validateName(formData.lastName)) newErrors.lastName = 'Last name: letters only, 2-100 chars';
        break;
      case 2:
        if (!validateContact(formData.contactNumber)) newErrors.contactNumber = 'Contact: +CCxxxxxxxxxx (digits only)';
        break;
      case 3:
        if (!validateEmail(formData.email)) newErrors.email = 'Enter email end with .com (eg. test@gmail.com)';
        break;
      case 4:
        if (!validateUsername(formData.username)) newErrors.username = 'Username: Unique name. Use letters, digits only, 1-100 chars';
        break;
      case 5:
        if (!validatePassword(formData.password, formData.confirmPassword)) newErrors.password = 'Password: 5-100 chars, matches confirmation';
        break;
      case 6:
        if (!validateLocation(formData.city)) newErrors.city = 'Enter valid city';
        if (!validateLocation(formData.state)) newErrors.state = 'Enter valid state';
        if (!validateLocation(formData.country)) newErrors.country = 'Enter valid country';
        break;
      case 7:
        // Referral code optional, no validation
        break;
      case 8:
        if (!validateSecurityAnswer(formData.securityAnswer)) newErrors.securityAnswer = '';
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, step]);

  // Validate all fields (client-side) before API submit
  const validateAll = () => {
    let newErrors = {};
    if (!validateName(formData.firstName)) newErrors.firstName = 'First name: letters only, 2-100 chars';
    if (!validateName(formData.lastName)) newErrors.lastName = 'Last name: letters only, 2-100 chars';
    if (!validateContact(formData.contactNumber)) newErrors.contactNumber = 'Contact: +CCxxxxxxxxxx (digits only)';
    if (!validateEmail(formData.email)) newErrors.email = 'Enter email end with .com (eg. test@gmail.com)';
    if (!validateUsername(formData.username)) newErrors.username = 'Username: Unique name. Use letters, digits only, 1-100 chars';
    if (!validatePassword(formData.password, formData.confirmPassword)) newErrors.password = 'Password: 5-100 chars, matches confirmation';
    if (!validateLocation(formData.city)) newErrors.city = 'Enter valid city';
    if (!validateLocation(formData.state)) newErrors.state = 'Enter valid state';
    if (!validateLocation(formData.country)) newErrors.country = 'Enter valid country';
    if (!validateSecurityAnswer(formData.securityAnswer)) newErrors.securityAnswer = '';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Next button handler
  const handleNext = () => {
    if (validateCurrentStep()) {
      setStep((prev) => prev + 1);
      setServerError(null);
    }
  };

  // Previous button handler
  const handlePrevious = () => {
    setStep((prev) => prev - 1);
    setErrors({});
    setServerError(null);
  };

  // Submit handler
  const handleSubmit = async () => {
    if (validateAll()) {
      setIsLoading(true);
      setServerError(null);
      try {
        apiPayload.set('load.pageId', 'Page30003');
        apiPayload.set('load.redirectRoute', '/join');
        apiPayload.set('load.data.userId', -1);
        apiPayload.set('load.data.firstName', formData.firstName);
        apiPayload.set('load.data.lastName', formData.lastName);
        apiPayload.set('load.data.contactNumber', formData.contactNumber);
        apiPayload.set('load.data.email', formData.email,);
        apiPayload.set('load.data.username', formData.username);
        apiPayload.set('load.data.password', formData.password);
        apiPayload.set('load.data.city', formData.city);
        apiPayload.set('load.data.state', formData.state);
        apiPayload.set('load.data.country', formData.country);
        apiPayload.set('load.data.referralCode', formData.referralCode);
        apiPayload.set('load.data.securityAnswer', formData.securityAnswer);
        handlePostApi("/v1/join", apiPayload.fetchPayload());
      } catch (err) {
        setServerError({ general: 'API error', error: error });
        console.warn(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <label><b>First Name</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['firstName'] = el)} name="firstName" value={formData.firstName} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('firstName')} onFocus={() => isMobile && setActiveInput('firstName')} readOnly={isMobile}/>
            {errors.firstName && <span className="error">{errors.firstName}</span>}
            <br/><br/>
            <label><b>Last Name</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['lastName'] = el)} name="lastName" value={formData.lastName} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('lastName')} onFocus={() => isMobile && setActiveInput('lastName')} readOnly={isMobile}/>
            {errors.lastName && <span className="error">{errors.lastName}</span>}
          </div>
        );
      case 2:
        return (
          <div>
            <label><b>Contact Number</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['contactNumber'] = el)} name="contactNumber" value={formData.contactNumber} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('contactNumber')} onFocus={() => isMobile && setActiveInput('contactNumber')} readOnly={isMobile}/>
            {errors.contactNumber && <span className="error">{errors.contactNumber}</span>}
            {serverError?.contactNumber && <span className="error">{serverError.contactNumber}</span>}
          </div>
        );
      case 3:
        return (
          <div>
            <label><b>Contact Email</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['email'] = el)} name="email" value={formData.email} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('email')} onFocus={() => isMobile && setActiveInput('email')} readOnly={isMobile}/>
            {errors.email && <span className="error">{errors.email}</span>}
            {serverError?.email && <span className="error">{serverError.email}</span>}
          </div>
        );
      case 4:
        return (
          <div>
            <label><b>Username</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['username'] = el)} name="username" value={formData.username} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('username')} onFocus={() => isMobile && setActiveInput('username')} readOnly={isMobile}/>
            {errors.username && <span className="error">{errors.username}</span>}
            {serverError?.username && <span className="error">{serverError.username}</span>}
          </div>
        );
      case 5:
        return (
          <div>
            <label><b>Password</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['password'] = el)} type="password" name="password" value={formData.password} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('password')} onFocus={() => isMobile && setActiveInput('password')} readOnly={isMobile}/>
            <br/><br/>
            <label><b>Confirm Password</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['confirmPassword'] = el)} type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('confirmPassword')} onFocus={() => isMobile && setActiveInput('confirmPassword')} readOnly={isMobile}/>
            {errors.password && <span className="error">{errors.password}</span>}
          </div>
        );
      case 6:
        return (
          <div>
            <label><b>City</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['city'] = el)} name="city" value={formData.city} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('city')} onFocus={() => isMobile && setActiveInput('city')} readOnly={isMobile}/>
            {errors.city && <span className="error">{errors.city}</span>}
            <br/><br/>
            <label><b>State</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['state'] = el)} name="state" value={formData.state} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('state')} onFocus={() => isMobile && setActiveInput('state')} readOnly={isMobile}/>
            {errors.state && <span className="error">{errors.state}</span>}
            <br/><br/>
            <label><b>Country</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['country'] = el)} name="country" value={formData.country} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('country')} onFocus={() => isMobile && setActiveInput('country')} readOnly={isMobile}/>
            {errors.country && <span className="error">{errors.country}</span>}
          </div>
        );
      case 7:
        return (
          <div>
            <label><b>Referral Code</b> (optional)</label>
            <br/>
            <input ref={(el) => (inputRefs.current['referralCode'] = el)} name="referralCode" value={formData.referralCode} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('referralCode')} onFocus={() => isMobile && setActiveInput('referralCode')} readOnly={isMobile}/>
          </div>
        );
      case 8:
        return (
          <div>
            <label><b>Your Favourite Color?</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['securityAnswer'] = el)} name="securityAnswer" value={formData.securityAnswer} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('securityAnswer')} onFocus={() => isMobile && setActiveInput('securityAnswer')} readOnly={isMobile}/>
            {errors.securityAnswer && <span className="error">{errors.securityAnswer}</span>}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Handle virtual key press
  const handleKeyPress = (key) => {
    if (activeInput) {
      let value = formData[activeInput];
      if (key === 'backspace') value = value.slice(0, -1);
      else value += key;
      setFormData((prev) => ({ ...prev, [activeInput]: value }));
    }
  };

  // Handle keyboard close
  const handleCloseKeyboard = () => setActiveInput(null);

  useEffect(() => {
    validateCurrentStep();
    if (data && data.code === "200") {
      let p_result = data.result;
      let p_name = formData.firstName;
      navigate('/code-verification', { state: { p_result, p_name } });
    } else if (data && data.code !== "200") {
      setServerError({ general: data.result.api_request_data.error });
    }
  }, [formData, validateCurrentStep, data, navigate]);

  return (
    <div className="join-page" style={{ position: 'relative' }}>
      {initLoader && <CloudLoader />}
      {isLoading && <CloudLoader />}
      {loading && <CloudLoader />}
      <h2>Join with us</h2>
      <hr style={{border:'none', height:'2px', background:'linear-gradient(to right, #e600c7 0%, #940180 25%, rgb(0, 0, 0) 100%)', boxShadow: 'rgba(0, 0, 0, 0.5) 0px 1px 3px inset', margin:'20px 0px', width:'100%'}} />
      <p><b>{step}</b> / 8</p>
      <br/>
      {renderStep()}
      {activeInput && isMobile && (
        <VirtualKeyboard
          onKeyPress={handleKeyPress}
          onClose={handleCloseKeyboard}
          isVisible={isMobile && !!activeInput}
        />
      )}
      {serverError?.general && <span className="error">{serverError.general}</span>}
      <div className="buttons">
        {step > 1 && <button className="btn" onClick={handlePrevious}>Previous</button>}
        {step < 8 && <button className="btn" onClick={handleNext} disabled={Object.keys(errors).length > 0}>Next</button>}
        {step === 8 && <button className="btn" onClick={handleSubmit} disabled={Object.keys(errors).length > 0}>Generate Access Code</button>}
      </div>
      <hr style={{border:'none', height:'2px', background:'linear-gradient(to right, #e600c7 0%, #940180 25%, rgb(0, 0, 0) 100%)', boxShadow: 'rgba(0, 0, 0, 0.5) 0px 1px 3px inset', margin:'20px 0px', width:'100%'}} />
      <button className="nav-item" onClick={() => navigate("/connect")}>Have Account? Connect us</button>
    </div>
  );
};

export default JoinUs;
